import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { buttonClasses, filterClasses, paginationClasses } from "../../common/tailwindClassnames";
import SelectInput from "../../common/SelectInput";
import Pagination from "../../common/Pagination";
import { page_size } from "../../../api/apiUtils";
import { getDestinationAccount } from "../../destinations/DestinationHelperFunctions";

type Candidate = { id: string; name: string; base_url?: string; type?: { id: string; name: string }; configurations?: any[] };
type FilterOption = { label: string; value: string };

type AddDestinationDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    candidates: Candidate[];
    loading: boolean;
    onAttach: (ids: string[]) => Promise<void> | void;
    onCreateNew: () => void;
    onSearch: (searchStr: string) => void;
    // The same numbered pagination the Routes list draws: the API's total
    // for the current query, the offset of the page on screen, and a ReactPaginate
    // page-click handler ({ selected }) that replaces the rows with that page.
    totalCount: number;
    pageOffset: number;
    onPageChange: (e: { selected: number }) => void;
    // Workspace filter — defaults to the Route's workspace, clearable to all.
    workspaceOptions: FilterOption[];
    workspaceFilter: FilterOption | null;
    onChangeWorkspaceFilter: (option: FilterOption | null) => void;
    loadingWorkspaces?: boolean;
};

// Same shell conventions as TransformationRulesPanel: fixed right-hand panel that
// slides in, header + scrollable body + sticky footer.
const AddDestinationDrawer = ({
    isOpen, onClose, candidates, loading, onAttach, onCreateNew, onSearch, totalCount, pageOffset, onPageChange,
    workspaceOptions, workspaceFilter, onChangeWorkspaceFilter, loadingWorkspaces,
}: AddDestinationDrawerProps) => {
    const { t } = useTranslation();
    const [checked, setChecked] = useState<Record<string, boolean>>({});
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState("");
    // The parent already loads the unfiltered list on open, so only queries the
    // user actually typed (debounced) go back up as a filtered reload.
    const lastSent = useRef<string | null>(null);

    useEffect(() => {
        if (!isOpen) { setChecked({}); setSaving(false); setQuery(""); lastSent.current = null; }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (lastSent.current === null) { lastSent.current = query; return; }
        if (query === lastSent.current) return;
        const timer = setTimeout(() => { lastSent.current = query; onSearch(query); }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, isOpen]);

    const selectedIds = Object.keys(checked).filter((id) => checked[id]);

    return (
        <div
            data-testid="AddDestinationDrawer-panel"
            className={`fixed top-0 right-0 h-full w-full md:w-[420px] bg-white border-l border-gray-lines shadow-xl z-[56] md:z-[60] transition-transform duration-300 flex flex-col ${
                isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"}`}
            aria-hidden={!isOpen}
            // The panel stays mounted so it can slide, so it also has to be taken
            // out of the tab order while closed — aria-hidden with focusable
            // children is an a11y violation. `inert` is not typed on React 18's
            // HTMLAttributes yet, hence the cast.
            {...({ inert: isOpen ? undefined : "" } as any)}>
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-lines">
                <h2 className="text-lg font-semibold text-off-black">{t("Add destination")}</h2>
                <button onClick={onClose} aria-label={t("Close")}
                    className="w-9 h-9 flex items-center justify-center text-medium-gray hover:text-off-black">✕</button>
            </div>

            <div className="px-4 pt-4 flex flex-col gap-3">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Search destinations")}
                    className="w-full border border-field-outline rounded px-3 py-2 text-sm text-off-black placeholder:text-secondary-text focus:outline-none focus:border-route-green"
                />
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="drawer_workspace"
                        options={workspaceOptions}
                        value={workspaceFilter}
                        onChange={onChangeWorkspaceFilter}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={!!loadingWorkspaces}
                        className=""
                        variant="routeFilter"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
                {loading
                    ? [0, 1, 2, 3].map((i) => <div key={i} className="h-10 w-full bg-neutral-gray-50 rounded animate-pulse mb-2" />)
                    : candidates.length === 0
                        ? <p className="text-sm text-secondary-text mb-4">
                            {query ? t("No destinations match your search.")
                                : workspaceFilter ? t("No other destinations in this workspace yet.")
                                    : t("No other destinations available.")}
                          </p>
                        : candidates.map((c) => (
                            <label key={c.id} className="flex items-start gap-3 py-2 border-b border-gray-lines cursor-pointer">
                                <input type="checkbox" className="mt-1 accent-route-green"
                                    checked={!!checked[c.id]}
                                    onChange={(e) => setChecked((prev) => ({ ...prev, [c.id]: e.target.checked }))} />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm text-off-black truncate">{c.name}</span>
                                    <span className="block text-xs text-secondary-text truncate">{c.base_url || t("No URL")}</span>
                                    {/* EarthRanger only: which ER account this destination logs in with */}
                                    {(() => { const account = getDestinationAccount(c as any); return account
                                        ? <span className="block text-xs text-secondary-text truncate">{t("Account")}: <span className="font-mono">{account}</span></span>
                                        : null; })()}
                                </span>
                                {c.type?.name && (
                                    <span className="flex-none bg-light-green text-route-green rounded-full px-2 py-0.5 text-[11px] font-semibold">
                                        {c.type.name}
                                    </span>
                                )}
                            </label>
                        ))}
                {totalCount > 0 && (
                    <Pagination
                        showText={true}
                        handlePageClick={onPageChange}
                        pageCount={Math.ceil(totalCount / page_size)}
                        resultStart={pageOffset + 1}
                        resultEnd={(pageOffset + page_size) < totalCount ? (pageOffset + page_size) : totalCount}
                        totalCount={totalCount}
                        initialPage={Math.floor(pageOffset / page_size)}
                        take={page_size}
                        loading={loading}
                        // The drawer is much narrower than a list page, so fewer
                        // page numbers fit before the row wraps.
                        pageRangeDisplayed={2}
                        marginPagesDisplayed={1}
                        mainclassname={paginationClasses.container}
                    />
                )}

                {/* Secondary path, deliberately not button-shaped: a full-width
                    button here sat closer to the rows than the footer's Add to
                    Route and got clicked when the intent was to select an
                    existing destination (GUNDI-5638). */}
                <p className="text-sm text-secondary-text mt-4">
                    {t("Can't find the destination you need?")}{" "}
                    <button type="button" onClick={onCreateNew}
                        data-testid="AddDestinationDrawer-create-new-button"
                        className={buttonClasses.inlineLink}>
                        {t("Create new destination")}
                    </button>
                </p>
            </div>

            <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-lines">
                {/* Checked rows survive a page change, so say how many are selected —
                    otherwise a selection made two pages back is invisible and the
                    user attaches more than the empty checkboxes on screen suggest. */}
                {selectedIds.length > 0 && (
                    <span className="text-sm text-secondary-text order-last ml-auto">
                        {t("{{count}} selected", { count: selectedIds.length })}
                    </span>
                )}
                <button className={buttonClasses.primary} disabled={selectedIds.length === 0 || saving}
                    onClick={async () => {
                        setSaving(true);
                        try { await onAttach(selectedIds); } finally { setSaving(false); }
                    }}>
                    {t("Add to Route")}
                </button>
                <button className={buttonClasses.cancel} onClick={onClose}>{t("Cancel")}</button>
            </div>
        </div>
    );
};

export default AddDestinationDrawer;
